import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Building2, LogOut, Users, UserPlus, CheckCircle2, XCircle, ClipboardList, Copy, Trash2, X, Loader2,
} from "lucide-react";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SchoolDashboard() {
  const nav = useNavigate();
  const token = localStorage.getItem("cc_school_token");
  const school = JSON.parse(localStorage.getItem("cc_school") || "null");
  const [data, setData] = useState({ students: [], pending: [], school_code: "" });
  const [loading, setLoading] = useState(true);
  const [addEmail, setAddEmail] = useState("");
  const [modal, setModal] = useState(null); // student being scored
  const [form, setForm] = useState({ recommended_stream: "", aptitude: "", interest: "", personality: "", notes: "" });

  const api = axios.create({ baseURL: BASE, headers: { Authorization: `Bearer ${token}` } });

  const load = useCallback(async () => {
    try {
      const r = await api.get("/school/students");
      setData(r.data);
    } catch {
      localStorage.removeItem("cc_school_token");
      nav("/school/login");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!token) { nav("/school/login"); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = () => {
    localStorage.removeItem("cc_school_token");
    localStorage.removeItem("cc_school");
    nav("/school/login");
  };

  const addStudent = async (e) => {
    e.preventDefault();
    if (!addEmail.trim()) return;
    try {
      const r = await api.post("/school/students/add", { email: addEmail.trim() });
      toast.success(r.data.linked ? "Student linked to your school" : "Invite saved — links when the student signs up");
      setAddEmail("");
      load();
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Could not add student");
    }
  };

  const removeStudent = async (id) => {
    await api.delete(`/school/students/${id}`);
    toast.success("Student removed");
    load();
  };

  const openScore = (s) => {
    setForm({ recommended_stream: "", aptitude: "", interest: "", personality: "", notes: "" });
    setModal(s);
  };

  const saveScore = async (e) => {
    e.preventDefault();
    const body = {
      recommended_stream: form.recommended_stream,
      aptitude: form.aptitude ? Number(form.aptitude) : null,
      interest: form.interest ? Number(form.interest) : null,
      personality: form.personality ? Number(form.personality) : null,
      notes: form.notes,
    };
    try {
      await api.post(`/school/students/${modal.id}/assessment`, body);
      toast.success("Assessment recorded");
      setModal(null);
      load();
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Could not save");
    }
  };

  const copyCode = () => {
    navigator.clipboard?.writeText(data.school_code);
    toast.success("School code copied");
  };

  const input = "w-full border border-gray-200 rounded-lg px-3.5 py-2.5 text-sm text-[#16244F] outline-none focus:ring-2 focus:ring-[#F58220]";

  if (loading) {
    return <div className="min-h-screen grid place-items-center bg-[#f7f8fb]"><Loader2 className="animate-spin text-[#F58220]" size={28} /></div>;
  }

  return (
    <div className="min-h-screen bg-[#f7f8fb]">
      <header className="bg-[#16244F] text-white">
        <div className="max-w-[1140px] mx-auto px-5 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#F58220] grid place-items-center"><Building2 size={20} /></div>
            <div>
              <h1 className="font-head text-lg font-bold leading-tight" data-testid="school-name">{school?.school_name}</h1>
              <p className="text-xs text-white/60">{school?.official_name} · {school?.designation}</p>
            </div>
          </div>
          <button onClick={logout} data-testid="school-logout" className="inline-flex items-center gap-2 text-sm font-semibold border border-white/25 hover:bg-white/10 px-3.5 py-2 rounded-lg transition-colors">
            <LogOut size={16} /> Logout
          </button>
        </div>
      </header>

      <main className="max-w-[1140px] mx-auto px-5 py-8">
        <div className="grid sm:grid-cols-3 gap-4 mb-7">
          <div className="bg-white rounded-2xl cc-shadow-card p-5">
            <div className="flex items-center gap-2 text-[#8a90a0] text-sm"><Users size={16} /> Students</div>
            <div className="font-head text-3xl font-bold text-[#16244F] mt-1" data-testid="stat-students">{data.students.length}</div>
          </div>
          <div className="bg-white rounded-2xl cc-shadow-card p-5">
            <div className="flex items-center gap-2 text-[#8a90a0] text-sm"><ClipboardList size={16} /> Assessments recorded</div>
            <div className="font-head text-3xl font-bold text-[#16244F] mt-1" data-testid="stat-assessments">{data.students.filter((s) => s.assessment_taken).length}</div>
          </div>
          <div className="bg-white rounded-2xl cc-shadow-card p-5">
            <div className="flex items-center gap-2 text-[#8a90a0] text-sm">School Code</div>
            <button onClick={copyCode} data-testid="school-code" className="font-head text-2xl font-bold text-[#F58220] mt-1 inline-flex items-center gap-2 hover:opacity-80">
              {data.school_code} <Copy size={16} />
            </button>
            <p className="text-xs text-[#8a90a0] mt-1">Students can enter this at signup</p>
          </div>
        </div>

        <form onSubmit={addStudent} className="bg-white rounded-2xl cc-shadow-card p-5 mb-7 flex flex-col sm:flex-row gap-3 sm:items-end" data-testid="add-student-form">
          <div className="flex-1">
            <label className="block text-sm font-semibold text-[#16244F] mb-1.5">Add a student by email</label>
            <input className={input} type="email" placeholder="student@email.com" value={addEmail} onChange={(e) => setAddEmail(e.target.value)} data-testid="add-student-email" />
          </div>
          <button type="submit" data-testid="add-student-submit" className="inline-flex items-center justify-center gap-2 bg-[#16244F] hover:bg-[#101a3a] text-white font-head font-semibold text-sm px-5 py-2.5 rounded-lg transition-colors">
            <UserPlus size={16} /> Add Student
          </button>
        </form>

        <div className="bg-white rounded-2xl cc-shadow-card overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100">
            <h2 className="font-head text-lg font-bold text-[#16244F]">Student Reports</h2>
          </div>
          {data.students.length === 0 ? (
            <div className="p-12 text-center text-[#8a90a0]" data-testid="roster-empty">
              No students linked yet. Share your school code or add students by email.
            </div>
          ) : (
            <div className="divide-y divide-gray-50">
              {data.students.map((s) => (
                <div key={s.id} className="p-5" data-testid={`student-row-${s.id}`}>
                  <div className="flex flex-col md:flex-row md:items-start gap-4">
                    <div className="flex-1">
                      <div className="font-head font-semibold text-[#16244F]">{s.name}</div>
                      <div className="text-sm text-[#8a90a0]">{s.email} · {s.phone}</div>
                      <div className="flex flex-wrap items-center gap-2 mt-2">
                        <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full ${s.assessment_taken ? "bg-[#16a34a]/15 text-[#16a34a]" : "bg-gray-100 text-[#8a90a0]"}`}>
                          {s.assessment_taken ? <CheckCircle2 size={12} /> : <XCircle size={12} />} Assessment
                        </span>
                        <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full ${s.appointment_booked ? "bg-[#F58220]/15 text-[#F58220]" : "bg-gray-100 text-[#8a90a0]"}`}>
                          {s.appointment_booked ? <CheckCircle2 size={12} /> : <XCircle size={12} />} Appointment
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => openScore(s)} data-testid={`record-assessment-${s.id}`} className="text-sm font-semibold text-white bg-[#F58220] hover:bg-[#e0730f] px-3.5 py-2 rounded-lg transition-colors">Record Assessment</button>
                      <button onClick={() => removeStudent(s.id)} data-testid={`remove-student-${s.id}`} className="text-[#c3c8d4] hover:text-red-500 p-2"><Trash2 size={16} /></button>
                    </div>
                  </div>
                  {s.assessments.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {s.assessments.map((a) => (
                        <div key={a.id} className="bg-[#f7f8fb] rounded-lg px-4 py-3 text-sm">
                          <div className="flex items-center justify-between">
                            <span className="font-semibold text-[#16244F]">Recommended: {a.recommended_stream}</span>
                            <span className="text-xs text-[#8a90a0]">{new Date(a.created_at).toLocaleDateString()}</span>
                          </div>
                          <div className="flex flex-wrap gap-4 mt-1.5 text-xs text-[#45506b]">
                            {a.aptitude != null && <span>Aptitude: <b>{a.aptitude}</b></span>}
                            {a.interest != null && <span>Interest: <b>{a.interest}</b></span>}
                            {a.personality != null && <span>Personality: <b>{a.personality}</b></span>}
                          </div>
                          {a.notes && <p className="text-xs text-[#5a6274] mt-1.5">{a.notes}</p>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          {data.pending.length > 0 && (
            <div className="px-5 py-4 border-t border-gray-100">
              <p className="text-xs font-semibold text-[#8a90a0] uppercase tracking-wide mb-2">Pending invites (not yet signed up)</p>
              <div className="flex flex-wrap gap-2">
                {data.pending.map((p) => (
                  <span key={p.email} className="text-xs bg-gray-100 text-[#45506b] px-2.5 py-1 rounded-full">{p.email}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>

      {modal && (
        <div className="fixed inset-0 bg-black/40 grid place-items-center z-50 p-4" onClick={() => setModal(null)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="assessment-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-head text-lg font-bold text-[#16244F]">Record Assessment — {modal.name}</h3>
              <button onClick={() => setModal(null)}><X size={20} className="text-[#8a90a0]" /></button>
            </div>
            <form onSubmit={saveScore} className="space-y-3">
              <div>
                <label className="block text-sm font-semibold text-[#16244F] mb-1.5">Recommended Stream</label>
                <input className={input} value={form.recommended_stream} onChange={(e) => setForm({ ...form, recommended_stream: e.target.value })} required placeholder="e.g. Engineering & Technology" data-testid="score-stream" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-[#16244F] mb-1.5">Aptitude</label>
                  <input className={input} type="number" min="0" max="100" value={form.aptitude} onChange={(e) => setForm({ ...form, aptitude: e.target.value })} data-testid="score-aptitude" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#16244F] mb-1.5">Interest</label>
                  <input className={input} type="number" min="0" max="100" value={form.interest} onChange={(e) => setForm({ ...form, interest: e.target.value })} data-testid="score-interest" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#16244F] mb-1.5">Personality</label>
                  <input className={input} type="number" min="0" max="100" value={form.personality} onChange={(e) => setForm({ ...form, personality: e.target.value })} data-testid="score-personality" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#16244F] mb-1.5">Notes</label>
                <textarea className={input} rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="score-notes" />
              </div>
              <button type="submit" data-testid="score-save" className="w-full bg-[#F58220] hover:bg-[#e0730f] text-white font-head font-semibold text-sm px-5 py-3 rounded-lg transition-colors">Save Assessment</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
